import Modal from '../ui/Modal';
import { useState } from 'react';
import TonalitySection from '../ui/TonalitySection';
import { splitRoles, nameWithInitial, migrateInstruments, activeMusicians } from '../helpers';

// 116px: a 18px "Proposto da" e "Piano/Keys" sono larghi un centinaio di
// pixel. Con 80 il testo usciva dalla colonna e finiva sotto il valore accanto.
const LABEL = { width: 116, fontSize: 18, color: 'var(--muted)', flexShrink: 0 };
// Nome toccabile: apre la scheda del musicista. Bianco e sottolineato, non arancione.
const NOME_LINK = { background: 'none', border: 'none', padding: 0, minHeight: 44, cursor: 'pointer', fontFamily: 'inherit',
  fontSize: 18, color: 'var(--text)', textAlign: 'left', textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.35)',
  textUnderlineOffset: 4, textDecorationThickness: 1 };
const FIELD_ROW = { display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' };
const INLINE_INPUT = { flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text)', fontSize: 18, padding: 0, textAlign: 'left', width: '100%', minWidth: 0 };

const SLOTS = [
  { key: 'voce',     label: 'Voce',     match: r => r.toLowerCase().includes('voce') },
  { key: 'chitarra', label: 'Chitarra', match: r => r.toLowerCase().includes('chitar') },
  { key: 'tastiere', label: 'Piano/Keys', match: r => r.toLowerCase().includes('tast') || r.toLowerCase().includes('piano') || r.toLowerCase().includes('keys') },
  { key: 'basso',    label: 'Basso',    match: r => r.toLowerCase().includes('basso') },
  { key: 'batteria', label: 'Batteria', match: r => r.toLowerCase().includes('batter') },
  { key: 'altro',    label: 'Altro',    match: r => !['voce','chitar','tast','piano','keys','basso','batter'].some(k => r.toLowerCase().includes(k)) },
];

function TrackModal({ onClose, onSave, musician, existing, canManage, musicians, onRemovePlayer, onAddPlayer, onDelete, onShowMusician }) {
  const isEdit = !!existing;
  const players = existing?.track_players || [];

  // Chi ha proposto il brano. Si cerca nell'elenco completo, non fra i soli
  // musicisti attivi: un brano proposto da chi ha poi cancellato l'account
  // deve continuare a mostrare 'Utente cancellato', non sparire.
  const proposto = existing?.proposed_by
    ? (musicians || []).find(m => m.id === existing.proposed_by)
    : null;

  const myExistingEntry = players.find(p => p.musician_id === musician.id);
  const myExistingRoles = myExistingEntry
    ? splitRoles(myExistingEntry.role.replace('(alt)', '').trim())
    : [];

  const [multiMode, setMultiMode] = useState(false);
  const [multiTitles, setMultiTitles] = useState('');
  const [title, setTitle] = useState(existing?.title || '');
  const [artist, setArtist] = useState(existing?.artist || '');
  const [key, setKey] = useState(existing?.key || '');
  const [bpm, setBpm] = useState(existing?.bpm || '');
  const [hasVoice, setHasVoice] = useState(() => myExistingRoles.some(r => r.toLowerCase().includes('voce')));
  const [instrument, setInstrument] = useState(() => myExistingRoles.find(r => !r.toLowerCase().includes('voce')) || null);
  const [tonality, setTonality] = useState(() => myExistingEntry?.tonality || 'originale');
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRemovePlayer, setConfirmRemovePlayer] = useState(null);
  const [addingPlayer, setAddingPlayer] = useState(false);
  const [selectedAddMusician, setSelectedAddMusician] = useState('');

  const availableRoles = [...(musician.sings ? ['Voce'] : []), ...migrateInstruments(musician.instruments || [])];
  const roleStr = [hasVoice && 'Voce', instrument].filter(Boolean).join(', ');

  const handleSlotTap = (slot) => {
    if (slot.key === 'voce') {
      setHasVoice(prev => !prev);
    } else {
      const matchingRole = availableRoles.find(r => slot.match(r));
      if (!matchingRole) return;
      setInstrument(prev => prev === matchingRole ? null : matchingRole);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (!isEdit && multiMode) {
        const lines = multiTitles.split('\n').map(l => l.trim()).filter(Boolean);
        await onSave({ titles: lines, role: roleStr, tonality });
      } else {
        await onSave({ title, artist, key, bpm: bpm ? parseInt(bpm) : null, role: roleStr, tonality });
      }
    } finally {
      setLoading(false);
    }
  };

  const canSave = isEdit ? title.trim() : multiMode ? multiTitles.trim() : title.trim();

  return (
    <Modal onClose={onClose}>
      <div className="modal-title" style={{ marginBottom: 20 }}>
        {isEdit ? 'Modifica brano' : 'Proponi un brano'}
      </div>

      {/* Switcher singolo/multiplo */}
      {!isEdit && (
        <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
          {[['Singolo', false], ['Più brani', true]].map(([label, val]) => (
            <button key={label} onClick={() => setMultiMode(val)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0',
              fontSize: 16, fontWeight: multiMode === val ? 700 : 400,
              color: multiMode === val ? 'var(--text)' : 'var(--muted)',
              borderBottom: multiMode === val ? '2px solid #ED4D1D' : '2px solid transparent',
              transition: 'all .15s',
            }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Modalità multiplo */}
      {!isEdit && multiMode ? (
        <div style={{ marginBottom: 16 }}>
          <textarea className="form-textarea" rows={5}
            placeholder={'Superstition\nHey Jude\nCiao mamma'}
            value={multiTitles} onChange={e => setMultiTitles(e.target.value)} />
          <div style={{ fontSize: 16, color: 'var(--muted)', marginTop: 4 }}>
            {multiTitles.split('\n').filter(l => l.trim()).length} brani
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 4 }}>
          <div style={FIELD_ROW}>
            <span style={LABEL}>Brano</span>
            <input style={INLINE_INPUT} placeholder="es. Superstition"
              value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div style={FIELD_ROW}>
            <span style={LABEL}>Artista</span>
            <input style={INLINE_INPUT} placeholder="es. Stevie Wonder"
              value={artist} onChange={e => setArtist(e.target.value)} />
          </div>
          <div style={FIELD_ROW}>
            <span style={LABEL}>Tonalità</span>
            <input style={INLINE_INPUT} placeholder="es. La min"
              value={key} onChange={e => setKey(e.target.value)} />
          </div>
          <div style={{ ...FIELD_ROW, borderBottom: 'none' }}>
            <span style={LABEL}>BPM</span>
            <input style={INLINE_INPUT} placeholder="es. 120" type="number" min="40" max="240"
              value={bpm} onChange={e => setBpm(e.target.value)} />
          </div>
        </div>
      )}

      {/* Proposto da */}
      {isEdit && proposto && (
        <div style={{ ...FIELD_ROW, borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: 'none' }}>
          <span style={LABEL}>Proposto da</span>
          {onShowMusician ? (
            <button onClick={() => onShowMusician(proposto)} style={NOME_LINK}>
              {nameWithInitial(proposto.name)}
            </button>
          ) : (
            <span style={{ fontSize: 18, color: 'var(--text)' }}>{nameWithInitial(proposto.name)}</span>
          )}
        </div>
      )}

      {/* Chi suona */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12, marginTop: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--muted)', letterSpacing: '1px', marginBottom: 4 }}>
          CHI SUONA
        </div>
        {SLOTS.map((slot, idx) => {
          const isLast = idx === SLOTS.length - 1;
          const iCanPlay = availableRoles.some(r => slot.match(r));
          const iAmSelected = slot.key === 'voce' ? hasVoice : !!(instrument && slot.match(instrument));
          const slotOtherPlayers = players.filter(p =>
            p.musician_id !== musician.id &&
            splitRoles(p.role.replace('(alt)', '').trim()).some(r => slot.match(r))
          );

          const indicator = iCanPlay ? (
            slot.key === 'voce' ? (
              /* Checkbox per Voce */
              <svg width="16" height="16" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                <rect x="1" y="1" width="14" height="14" rx="3"
                  fill={iAmSelected ? '#ED4D1D' : 'none'}
                  stroke={iAmSelected ? '#ED4D1D' : 'rgba(255,255,255,0.3)'} strokeWidth="1.5"/>
                {iAmSelected && <polyline points="4,8 7,11 12,5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>}
              </svg>
            ) : (
              /* Radio per strumenti esclusivi */
              <svg width="16" height="16" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                <circle cx="8" cy="8" r="7"
                  fill="none"
                  stroke={iAmSelected ? '#ED4D1D' : 'rgba(255,255,255,0.3)'} strokeWidth="1.5"/>
                {iAmSelected && <circle cx="8" cy="8" r="4" fill="#ED4D1D"/>}
              </svg>
            )
          ) : <span style={{ width: 16, flexShrink: 0 }} />;

          return (
            <div key={slot.key}
              onClick={() => iCanPlay && handleSlotTap(slot)}
              style={{ ...FIELD_ROW, cursor: iCanPlay ? 'pointer' : 'default',
                borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.07)' }}>
              {indicator}
              <span style={{ ...LABEL, marginLeft: 8, color: iCanPlay || iAmSelected ? 'var(--text)' : 'var(--muted)', fontWeight: iAmSelected ? 600 : 400 }}>
                {slot.label}
              </span>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', columnGap: 12, rowGap: 4, flexWrap: 'wrap' }}>
                {iAmSelected && (
                  <span style={{ fontSize: 18, color: 'var(--text)', fontWeight: 700 }}>
                    {nameWithInitial(musician.name)}
                  </span>
                )}
                {slotOtherPlayers.map(p => {
                  const m = musicians?.find(mu => mu.id === p.musician_id);
                  if (!m) return null;
                  return confirmRemovePlayer?.id === p.musician_id ? (
                    <div key={p.musician_id} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                      onClick={e => e.stopPropagation()}>
                      <span style={{ fontSize: 16, color: 'var(--text)' }}>Rimuovere {nameWithInitial(m.name)}?</span>
                      <button className="btn btn-secondary btn-sm" onClick={() => setConfirmRemovePlayer(null)}>No</button>
                      <button className="btn btn-danger btn-sm" onClick={async () => { await onRemovePlayer(p.musician_id); setConfirmRemovePlayer(null); }}>Sì</button>
                    </div>
                  ) : (
                    <span key={p.musician_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      {onShowMusician ? (
                        <button onClick={e => { e.stopPropagation(); onShowMusician(m); }} style={NOME_LINK}>
                          {nameWithInitial(m.name)}
                        </button>
                      ) : (
                        <span style={{ fontSize: 18, color: 'var(--text)' }}>{nameWithInitial(m.name)}</span>
                      )}
                      {canManage && (
                        <button onClick={e => { e.stopPropagation(); setConfirmRemovePlayer({ id: p.musician_id, name: m.name }); }}
                          aria-label={`Togli ${nameWithInitial(m.name)}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted2)', fontSize: 22, lineHeight: 1,
                            minWidth: 36, minHeight: 44, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          ×
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}

        {canManage && musicians && !addingPlayer && (
          <button className="btn btn-secondary btn-sm" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}
            onClick={() => setAddingPlayer(true)}>
            + Aggiungi musicista
          </button>
        )}
        {canManage && addingPlayer && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <select className="form-select" style={{ flex: 1, fontSize: 16 }}
              value={selectedAddMusician} onChange={e => setSelectedAddMusician(e.target.value)}>
              <option value="">Scegli musicista…</option>
              {activeMusicians(musicians)
                .filter(m => !players.some(p => p.musician_id === m.id))
                .map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button className="btn btn-teal btn-sm" disabled={!selectedAddMusician}
              onClick={async () => {
                const m = musicians.find(mu => mu.id === selectedAddMusician);
                if (!m) return;
                const role = [m.default_sings && 'Voce', m.default_instrument].filter(Boolean).join(', ') || (m.instruments || [])[0] || '';
                await onAddPlayer(selectedAddMusician, role);
                setAddingPlayer(false); setSelectedAddMusician('');
              }}>OK</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setAddingPlayer(false)}>✕</button>
          </div>
        )}
      </div>

      <TonalitySection role={roleStr} tonality={tonality} setTonality={setTonality} />

      {/* Elimina */}
      {isEdit && onDelete && (
        confirmDelete ? (
          <div style={{ marginTop: 14, padding: '10px 12px', background: 'rgba(160,80,80,0.08)', border: '1px solid rgba(160,80,80,0.2)', borderRadius: 10 }}>
            <div style={{ fontSize: 16, color: 'var(--text)', marginBottom: 10 }}>
              Eliminare questo brano? Verranno rimossi tutti i musicisti candidati.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(false)}>No</button>
              <button className="btn btn-danger btn-sm" onClick={async () => { await onDelete(); setConfirmDelete(false); }}>Sì, elimina</button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--muted2)', textDecoration: 'underline' }}
              onClick={() => setConfirmDelete(true)}>
              Elimina brano
            </button>
          </div>
        )
      )}

      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
        <button className="btn btn-primary" disabled={!canSave || loading} onClick={handleSave}>
          {loading ? <span className="spinner" /> : isEdit ? 'Salva' : multiMode ? `Aggiungi ${multiTitles.split('\n').filter(l => l.trim()).length} brani` : 'Proponi'}
        </button>
      </div>
    </Modal>
  );
}

export default TrackModal;
